import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ExpensesByCategoryReport,
  ExpensesByMemberReport,
  ExpenseComparisonReport,
  ExpenseComparisonType,
  IncomeExpensesSavingsReport,
} from '@core/models';
import { endpoints, environment } from '@environment';
import { Observable } from 'rxjs';
import { suppressGlobalErrorFeedback } from '@core/interceptors';

@Injectable({ providedIn: 'root' })
export class ReportsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/${endpoints.v1.reports}`;

  getIncomeExpensesSavings(
    dateFrom: string,
    dateTo: string
  ): Observable<IncomeExpensesSavingsReport> {
    const params = new HttpParams().set('dateFrom', dateFrom).set('dateTo', dateTo);
    return this.http.get<IncomeExpensesSavingsReport>(
      `${this.baseUrl}/income-expenses-savings`,
      { params, context: suppressGlobalErrorFeedback() }
    );
  }

  getExpensesByCategory(
    dateFrom: string,
    dateTo: string,
    memberKeys: string[] = []
  ): Observable<ExpensesByCategoryReport> {
    return this.http.get<ExpensesByCategoryReport>(`${this.baseUrl}/expenses-by-category`, {
      params: this.withKeys(dateFrom, dateTo, 'memberKeys', memberKeys),
      context: suppressGlobalErrorFeedback(),
    });
  }

  getExpensesByMember(
    dateFrom: string,
    dateTo: string,
    categoryKeys: string[] = []
  ): Observable<ExpensesByMemberReport> {
    return this.http.get<ExpensesByMemberReport>(`${this.baseUrl}/expenses-by-member`, {
      params: this.withKeys(dateFrom, dateTo, 'categoryKeys', categoryKeys),
      context: suppressGlobalErrorFeedback(),
    });
  }

  getExpenseComparison(
    comparisonType: ExpenseComparisonType,
    baselinePeriod: string,
    comparedPeriod: string,
    categoryKeys: string[] = []
  ): Observable<ExpenseComparisonReport> {
    const params = categoryKeys.reduce(
      (value, key) => value.append('categoryKeys', key),
      new HttpParams()
        .set('comparisonType', comparisonType)
        .set('baselinePeriod', baselinePeriod)
        .set('comparedPeriod', comparedPeriod)
    );
    return this.http.get<ExpenseComparisonReport>(`${this.baseUrl}/expense-comparison`, { params, context: suppressGlobalErrorFeedback() });
  }

  private withKeys(dateFrom: string, dateTo: string, keyName: string, keys: string[]): HttpParams {
    return keys.reduce(
      (params, key) => params.append(keyName, key),
      new HttpParams().set('dateFrom', dateFrom).set('dateTo', dateTo)
    );
  }
}
